import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, CheckCircle2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { SERVICE_LIBRARY, DAY_LABELS, SECONDARY_IMAGE } from "@/lib/constants";
import { toast } from "sonner";
import PhotoUploader from "@/components/PhotoUploader";

const STEPS = ["Basics", "Location", "Hours", "About", "Services", "Pricing", "Photos", "Final"];

const initialHours = Object.fromEntries(
  DAY_LABELS.map((d) => [d.key, { open: "09:00", close: "18:00", closed: false }])
);

export default function AddBusiness() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const [data, setData] = useState({
    name: "", type: "clinic", phone: "", email: "", website: "",
    address: "", city: "", state: "", zip_code: "",
    service_area_cities: [],
    hours: initialHours,
    description: "", tagline: "", years_in_business: 1, medical_director: "",
    specialties: [],
    services: [], // {name, description, price_min, price_max, duration_min}
    accepts_walkins: false, accepts_insurance: false, home_visits: false,
    licensed_nurses: true, languages: ["English"], payment_methods: ["Credit Card"],
    hero_image: "", gallery: [],
  });

  const set = (patch) => setData({ ...data, ...patch });

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data: res } = await api.post("/providers", data);
      setDone(res);
      toast.success("Your business has been submitted for review!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <CheckCircle2 className="w-16 h-16 text-[#10B981] mx-auto mb-6" />
        <h1 className="font-display text-3xl font-semibold mb-3">You're on the map!</h1>
        <p className="text-stone-600 mb-8">
          We've received your listing for <strong>{data.name}</strong>. Our team reviews submissions within 24–48 hours, then your business will be live on IV Therapy Finder.
        </p>
        <div className="flex gap-3 justify-center">
          <Button variant="outline" onClick={() => nav("/")}>Back to home</Button>
          <Button className="bg-[#064E3B] hover:bg-[#053d2f]" onClick={() => nav(`/listing/${done.slug}`)}>Preview listing</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#10B981] mb-2">List your business</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900 mb-2">Add your IV therapy practice</h1>
        <p className="text-stone-600">Get discovered by patients searching for IV therapy in your area. Free to list — takes about 5 minutes.</p>
      </div>

      <div className="mb-8">
        <div className="flex items-center justify-between text-xs mb-2">
          <span className="font-semibold text-stone-700">Step {step + 1} of {STEPS.length}: {STEPS[step]}</span>
          <span className="text-stone-500">{Math.round(((step + 1) / STEPS.length) * 100)}%</span>
        </div>
        <Progress value={((step + 1) / STEPS.length) * 100} className="h-1.5" />
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8 space-y-5">
        {step === 0 && <Basics data={data} set={set} />}
        {step === 1 && <Location data={data} set={set} />}
        {step === 2 && <Hours data={data} set={set} />}
        {step === 3 && <About data={data} set={set} />}
        {step === 4 && <Services data={data} set={set} />}
        {step === 5 && <Pricing data={data} set={set} />}
        {step === 6 && <Photos data={data} set={set} />}
        {step === 7 && <Review data={data} />}
      </div>

      <div className="flex items-center justify-between mt-6">
        <Button variant="outline" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        {step < STEPS.length - 1 ? (
          <Button className="bg-[#064E3B] hover:bg-[#053d2f]" onClick={() => setStep(step + 1)} data-testid={`wizard-next-${step}`}>
            Continue <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button className="bg-[#064E3B] hover:bg-[#053d2f]" onClick={submit} disabled={submitting} data-testid="wizard-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit listing <Check className="w-4 h-4 ml-2" /></>}
          </Button>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider font-semibold text-stone-700 mb-1.5 block">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      {children}
    </div>
  );
}

function Basics({ data, set }) {
  return (
    <>
      <h2 className="font-display text-xl font-semibold mb-3">Business basics</h2>
      <Field label="Business name" required>
        <Input value={data.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Pacific Coast IV Lounge" data-testid="wiz-name" />
      </Field>
      <Field label="Provider type" required>
        <Select value={data.type} onValueChange={(v) => set({ type: v })}>
          <SelectTrigger data-testid="wiz-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="clinic">IV Clinic (fixed location)</SelectItem>
            <SelectItem value="mobile">Mobile IV Service</SelectItem>
            <SelectItem value="both">Both — Clinic & Mobile</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Phone number" required>
          <Input value={data.phone} onChange={(e) => set({ phone: e.target.value })} placeholder="(555) 123-4567" data-testid="wiz-phone" />
        </Field>
        <Field label="Email">
          <Input type="email" value={data.email} onChange={(e) => set({ email: e.target.value })} placeholder="hello@clinic.com" />
        </Field>
      </div>
      <Field label="Website">
        <Input value={data.website} onChange={(e) => set({ website: e.target.value })} placeholder="https://yourbusiness.com" />
      </Field>
    </>
  );
}

function Location({ data, set }) {
  return (
    <>
      <h2 className="font-display text-xl font-semibold mb-3">Location & service area</h2>
      <Field label="Street address">
        <Input value={data.address} onChange={(e) => set({ address: e.target.value })} placeholder="123 Main St, Suite 100" data-testid="wiz-address" />
      </Field>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="City" required>
          <Input value={data.city} onChange={(e) => set({ city: e.target.value })} data-testid="wiz-city" />
        </Field>
        <Field label="State" required>
          <Input value={data.state} onChange={(e) => set({ state: e.target.value })} data-testid="wiz-state" />
        </Field>
        <Field label="ZIP">
          <Input value={data.zip_code} onChange={(e) => set({ zip_code: e.target.value })} />
        </Field>
      </div>
      {data.type !== "clinic" && (
        <Field label="Service area cities (comma-separated, for mobile providers)">
          <Input
            value={data.service_area_cities.join(", ")}
            onChange={(e) => set({ service_area_cities: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
            placeholder="Miami Beach, Brickell, Coral Gables"
          />
        </Field>
      )}
    </>
  );
}

function Hours({ data, set }) {
  return (
    <>
      <h2 className="font-display text-xl font-semibold mb-3">Operating hours</h2>
      <div className="space-y-2">
        {DAY_LABELS.map((d) => {
          const h = data.hours[d.key];
          return (
            <div key={d.key} className="grid grid-cols-[100px_auto_1fr_1fr] gap-3 items-center">
              <span className="text-sm font-medium text-stone-700">{d.label}</span>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={!h.closed}
                  onCheckedChange={(v) => set({ hours: { ...data.hours, [d.key]: { ...h, closed: !v } } })}
                  id={`open-${d.key}`}
                />
                <Label htmlFor={`open-${d.key}`} className="text-xs">Open</Label>
              </div>
              <Input type="time" disabled={h.closed} value={h.open} onChange={(e) => set({ hours: { ...data.hours, [d.key]: { ...h, open: e.target.value } } })} />
              <Input type="time" disabled={h.closed} value={h.close} onChange={(e) => set({ hours: { ...data.hours, [d.key]: { ...h, close: e.target.value } } })} />
            </div>
          );
        })}
      </div>
    </>
  );
}

function About({ data, set }) {
  return (
    <>
      <h2 className="font-display text-xl font-semibold mb-3">About your business</h2>
      <Field label="Tagline (one sentence)">
        <Input value={data.tagline} onChange={(e) => set({ tagline: e.target.value })} placeholder="Premier vitamin therapy lounge in West Hollywood" />
      </Field>
      <Field label="Description (up to 600 words)" required>
        <Textarea
          rows={6}
          value={data.description}
          onChange={(e) => set({ description: e.target.value })}
          placeholder="Tell patients what makes your practice special, your experience, your specialties, and why they should choose you."
          data-testid="wiz-description"
        />
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Years in business">
          <Input type="number" min="1" value={data.years_in_business} onChange={(e) => set({ years_in_business: parseInt(e.target.value) || 1 })} />
        </Field>
        <Field label="Medical director (if any)">
          <Input value={data.medical_director} onChange={(e) => set({ medical_director: e.target.value })} placeholder="Dr. Jane Smith, MD" />
        </Field>
      </div>
      <Field label="Specialties (comma-separated)">
        <Input
          value={data.specialties.join(", ")}
          onChange={(e) => set({ specialties: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
          placeholder="NAD+ Specialist, Athletic Recovery, Anti-Aging"
        />
      </Field>
    </>
  );
}

function Services({ data, set }) {
  const toggle = (name) => {
    const exists = data.services.find((s) => s.name === name);
    if (exists) {
      set({ services: data.services.filter((s) => s.name !== name) });
    } else {
      set({ services: [...data.services, { name, description: "", price_min: 150, price_max: 250, duration_min: 45 }] });
    }
  };
  return (
    <>
      <h2 className="font-display text-xl font-semibold mb-1">Services offered</h2>
      <p className="text-sm text-stone-500 mb-4">Select all services your practice provides. You'll set pricing on the next step.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {SERVICE_LIBRARY.map((s) => {
          const active = data.services.find((x) => x.name === s);
          return (
            <button
              key={s}
              onClick={() => toggle(s)}
              type="button"
              className={`px-3 py-2.5 text-sm rounded-lg border transition text-left ${active ? "bg-[#064E3B] text-white border-[#064E3B]" : "bg-white border-stone-200 hover:border-[#064E3B]/40"}`}
              data-testid={`service-toggle-${s.replace(/\s/g, '-').toLowerCase()}`}
            >
              {s}
            </button>
          );
        })}
      </div>
    </>
  );
}

function Pricing({ data, set }) {
  if (data.services.length === 0) {
    return <p className="text-sm text-stone-500">Please select services first.</p>;
  }
  return (
    <>
      <h2 className="font-display text-xl font-semibold mb-3">Pricing & duration</h2>
      <div className="space-y-3">
        {data.services.map((s, i) => (
          <div key={s.name} className="bg-stone-50 rounded-lg p-3">
            <div className="font-medium text-stone-900 mb-2">{s.name}</div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Min $</Label>
                <Input type="number" value={s.price_min} onChange={(e) => {
                  const arr = [...data.services]; arr[i] = { ...s, price_min: parseInt(e.target.value) || 0 }; set({ services: arr });
                }} />
              </div>
              <div>
                <Label className="text-xs">Max $</Label>
                <Input type="number" value={s.price_max} onChange={(e) => {
                  const arr = [...data.services]; arr[i] = { ...s, price_max: parseInt(e.target.value) || 0 }; set({ services: arr });
                }} />
              </div>
              <div>
                <Label className="text-xs">Min duration</Label>
                <Input type="number" value={s.duration_min} onChange={(e) => {
                  const arr = [...data.services]; arr[i] = { ...s, duration_min: parseInt(e.target.value) || 30 }; set({ services: arr });
                }} />
              </div>
            </div>
            <Textarea
              className="mt-2"
              rows={2}
              value={s.description}
              onChange={(e) => {
                const arr = [...data.services]; arr[i] = { ...s, description: e.target.value }; set({ services: arr });
              }}
              placeholder="Short description (optional)"
            />
          </div>
        ))}
      </div>
    </>
  );
}

function Photos({ data, set }) {
  return (
    <>
      <h2 className="font-display text-xl font-semibold mb-3">Photos</h2>
      <p className="text-sm text-stone-500 mb-4">Upload up to 10 photos of your space, team, and treatments. The first photo becomes your cover image.</p>
      <PhotoUploader
        photos={data.gallery}
        onChange={(g) => set({ gallery: g, hero_image: g[0] || "" })}
        max={10}
        label="Upload"
      />
    </>
  );
}

function Review({ data }) {
  return (
    <>
      <h2 className="font-display text-xl font-semibold mb-3">Final details</h2>
      <div className="grid sm:grid-cols-2 gap-2">
        {[
          ["accepts_walkins", "Accept walk-ins"],
          ["accepts_insurance", "Accept insurance"],
          ["home_visits", "Home & hotel visits"],
          ["licensed_nurses", "Licensed nurses on staff"],
        ].map(([k, l]) => (
          <label key={k} className="flex items-center gap-2.5 p-3 border border-stone-200 rounded-lg cursor-pointer hover:bg-stone-50">
            <Checkbox checked={data[k]} onCheckedChange={(v) => data[k] = v} disabled />
            <span className="text-sm">{l}</span>
          </label>
        ))}
      </div>
      <div className="mt-6 p-4 bg-stone-50 rounded-xl text-sm">
        <div className="font-display font-semibold mb-1">Ready to submit</div>
        <p className="text-stone-600">
          Your listing for <strong>{data.name || "your business"}</strong> in <strong>{data.city || "your city"}</strong> will be reviewed by our team within 24–48 hours.
        </p>
      </div>
    </>
  );
}
